import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { Invoice, InvoiceLineItem } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export async function renderConstructionInvoices(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const businessId = session?.businessId ?? 'preview';

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title"><i class="bi bi-receipt-cutoff"></i> Invoices</h1>
          <p class="page-subtitle">Create, track, and send invoices by client. Sorted most recent first.</p>
        </div>
        <div style="display:flex;gap:10px">
          <button class="btn btn-ghost" id="send-receipt-btn" style="display:none">
            <i class="bi bi-send"></i> Send
          </button>
          <button class="btn btn-gold" id="new-invoice-btn">
            <i class="bi bi-plus-lg"></i> New Invoice
          </button>
        </div>
      </div>
    </div>

    <!-- Invoice Modal -->
    <div id="invoice-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1000;align-items:flex-start;justify-content:center;overflow-y:auto;padding:24px 0">
      <div class="card" style="width:680px;margin:auto">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
          <h3 style="font-size:15px;font-weight:700;margin:0" id="invoice-modal-title">New Invoice</h3>
          <button class="btn btn-ghost btn-sm" id="close-invoice-modal"><i class="bi bi-x-lg"></i></button>
        </div>

        <!-- Client Info -->
        <div style="background:var(--bg-tertiary);border-radius:var(--radius-sm);padding:16px;margin-bottom:16px">
          <div style="font-size:11px;color:var(--text-muted);font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px">Client</div>
          <div class="form-row-2">
            <div>
              <label>Client Name *</label>
              <input type="text" id="inv-client" placeholder="ABC General Contractors" />
            </div>
            <div>
              <label>Invoice # *</label>
              <input type="text" id="inv-number" placeholder="INV-001" />
            </div>
          </div>
          <div class="form-row-2">
            <div>
              <label>Client Email</label>
              <input type="email" id="inv-email" placeholder="billing@client.com" />
            </div>
            <div>
              <label>Client Phone</label>
              <input type="tel" id="inv-phone" placeholder="+1 (360) 555-0100" />
            </div>
          </div>
        </div>

        <!-- Dates -->
        <div class="form-row-2" style="margin-bottom:16px">
          <div>
            <label>Issue Date *</label>
            <input type="date" id="inv-issued" />
          </div>
          <div>
            <label>Due Date *</label>
            <input type="date" id="inv-due" />
          </div>
        </div>

        <!-- Line Items -->
        <div style="margin-bottom:16px">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
            <div style="font-size:12px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">Line Items</div>
            <button class="btn btn-ghost btn-sm" id="add-line-btn"><i class="bi bi-plus"></i> Add Line</button>
          </div>
          <div id="line-items-container">
            <div style="display:grid;grid-template-columns:3fr 1fr 1fr 1fr 36px;gap:8px;margin-bottom:6px;font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.5px">
              <span>Description</span><span>Qty</span><span>Unit</span><span>Rate ($)</span><span></span>
            </div>
            <div id="line-items-list"></div>
          </div>
        </div>

        <!-- Totals -->
        <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
          <div style="min-width:260px">
            <div style="display:flex;justify-content:space-between;font-size:13px;padding:6px 0;border-bottom:1px solid var(--border)">
              <span style="color:var(--text-muted)">Subtotal</span>
              <span id="inv-subtotal" style="font-weight:600">$0.00</span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:center;font-size:13px;padding:6px 0;border-bottom:1px solid var(--border)">
              <span style="color:var(--text-muted)">Tax Rate</span>
              <div style="display:flex;align-items:center;gap:4px">
                <input type="number" id="inv-taxrate" value="0" min="0" max="30" step="0.1"
                  style="width:56px;text-align:right;font-size:13px" /> %
              </div>
            </div>
            <div style="display:flex;justify-content:space-between;font-size:13px;padding:6px 0;border-bottom:1px solid var(--border)">
              <span style="color:var(--text-muted)">Tax</span>
              <span id="inv-tax">$0.00</span>
            </div>
            <div style="display:flex;justify-content:space-between;font-size:16px;font-weight:800;padding:10px 0">
              <span>Total</span>
              <span id="inv-total" style="color:var(--brand-gold)">$0.00</span>
            </div>
          </div>
        </div>

        <div class="form-row" style="margin-bottom:16px">
          <label>Notes / Terms</label>
          <textarea id="inv-notes" placeholder="Payment due within 30 days. Late fee 1.5%/month…" style="min-height:60px"></textarea>
        </div>

        <div style="display:flex;gap:10px;justify-content:flex-end">
          <button class="btn btn-ghost" id="cancel-invoice-btn">Cancel</button>
          <button class="btn btn-ghost" id="save-draft-btn"><i class="bi bi-floppy"></i> Save Draft</button>
          <button class="btn btn-gold" id="save-send-btn"><i class="bi bi-send"></i> Save &amp; Send</button>
        </div>
      </div>
    </div>

    <!-- Send Modal (SMS/Email) -->
    <div id="send-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1001;align-items:center;justify-content:center">
      <div class="card" style="width:440px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:4px"><i class="bi bi-send"></i> Send Invoice</h3>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px">Send via email or text message</p>
        <div class="form-row">
          <label>Email Address</label>
          <input type="email" id="send-email" placeholder="client@example.com" />
        </div>
        <div class="form-row">
          <label>Phone (SMS)</label>
          <input type="tel" id="send-phone" placeholder="+1 (360) 555-0100" />
        </div>
        <div class="form-row" style="margin-bottom:16px">
          <label>Message</label>
          <textarea id="send-message" style="min-height:60px"></textarea>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end">
          <button class="btn btn-ghost" id="close-send-modal">Cancel</button>
          <button class="btn btn-ghost" id="do-send-sms"><i class="bi bi-chat-dots"></i> Send SMS</button>
          <button class="btn btn-gold" id="do-send-email"><i class="bi bi-envelope"></i> Send Email</button>
        </div>
      </div>
    </div>

    <div id="invoices-content">
      <div class="empty-state"><div class="spinner"></div></div>
    </div>
  `;

  let editingId: string | null = null;
  let lineItems: (InvoiceLineItem & { _id: string })[] = [];
  let activeSendInvoiceId: string | null = null;

  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  (document.getElementById('inv-issued') as HTMLInputElement).value = today;
  (document.getElementById('inv-due') as HTMLInputElement).value = in30;

  const openModal = (inv?: Invoice) => {
    editingId = inv?.id ?? null;
    lineItems = inv?.lineItems.map((l, i) => ({ ...l, _id: String(i) })) ?? [];
    (document.getElementById('invoice-modal-title') as HTMLElement).textContent = inv ? `Edit Invoice #${inv.invoiceNumber}` : 'New Invoice';
    (document.getElementById('inv-client') as HTMLInputElement).value = inv?.clientName ?? '';
    (document.getElementById('inv-number') as HTMLInputElement).value = inv?.invoiceNumber ?? `INV-${String(Date.now()).slice(-4)}`;
    (document.getElementById('inv-email') as HTMLInputElement).value = inv?.clientEmail ?? '';
    (document.getElementById('inv-phone') as HTMLInputElement).value = inv?.clientPhone ?? '';
    (document.getElementById('inv-issued') as HTMLInputElement).value = inv?.issuedDate ?? today;
    (document.getElementById('inv-due') as HTMLInputElement).value = inv?.dueDate ?? in30;
    (document.getElementById('inv-taxrate') as HTMLInputElement).value = String(inv?.taxRate ?? 0);
    (document.getElementById('inv-notes') as HTMLTextAreaElement).value = inv?.notes ?? '';
    renderLineItems();
    document.getElementById('invoice-modal')!.style.display = 'flex';
  };

  const closeModal = () => {
    document.getElementById('invoice-modal')!.style.display = 'none';
    editingId = null;
  };

  document.getElementById('new-invoice-btn')!.addEventListener('click', () => openModal());
  document.getElementById('close-invoice-modal')!.addEventListener('click', closeModal);
  document.getElementById('cancel-invoice-btn')!.addEventListener('click', closeModal);

  document.getElementById('add-line-btn')!.addEventListener('click', () => {
    lineItems.push({ _id: crypto.randomUUID(), description: '', quantity: 1, unit: 'ea', rate: 0, amount: 0 });
    renderLineItems();
  });

  document.getElementById('inv-taxrate')!.addEventListener('input', recalcTotals);

  const saveInvoice = async (sendAfter: boolean) => {
    const client = (document.getElementById('inv-client') as HTMLInputElement).value.trim();
    const number = (document.getElementById('inv-number') as HTMLInputElement).value.trim();
    if (!client || !number) { showToast('Client name and invoice number required', 'error'); return; }
    if (!lineItems.length) { showToast('Add at least one line item', 'error'); return; }

    const subtotal = lineItems.reduce((s, l) => s + l.amount, 0);
    const taxRate = parseFloat((document.getElementById('inv-taxrate') as HTMLInputElement).value) || 0;
    const tax = subtotal * (taxRate / 100);

    const payload = {
      businessId,
      clientName: client,
      clientEmail: (document.getElementById('inv-email') as HTMLInputElement).value.trim() || undefined,
      clientPhone: (document.getElementById('inv-phone') as HTMLInputElement).value.trim() || undefined,
      invoiceNumber: number,
      lineItems: lineItems.map(({ _id: _, ...l }) => l),
      subtotal,
      taxRate,
      tax,
      total: subtotal + tax,
      status: (sendAfter ? 'sent' : 'draft') as Invoice['status'],
      issuedDate: (document.getElementById('inv-issued') as HTMLInputElement).value,
      dueDate: (document.getElementById('inv-due') as HTMLInputElement).value,
      notes: (document.getElementById('inv-notes') as HTMLTextAreaElement).value.trim() || undefined,
    };

    try {
      const api = getAPI();
      if (editingId) {
        await api.updateInvoice(editingId, payload);
        showToast('Invoice updated', 'success');
      } else {
        await api.createInvoice(payload);
        showToast('Invoice saved', 'success');
      }
    } catch {
      showToast(editingId ? 'Invoice updated (preview)' : 'Invoice saved (preview)', 'info');
    }

    closeModal();
    await loadInvoices(businessId, openModal, openSendModal);

    if (sendAfter && payload.clientEmail) {
      openSendModal(null, payload.clientName, payload.clientEmail, payload.clientPhone ?? '', payload.invoiceNumber, payload.total);
    }
  };

  document.getElementById('save-draft-btn')!.addEventListener('click', () => saveInvoice(false));
  document.getElementById('save-send-btn')!.addEventListener('click', () => saveInvoice(true));

  // Send modal
  const openSendModal = (invoiceId: string | null, client: string, email: string, phone: string, number: string, total: number) => {
    activeSendInvoiceId = invoiceId;
    (document.getElementById('send-email') as HTMLInputElement).value = email;
    (document.getElementById('send-phone') as HTMLInputElement).value = phone;
    (document.getElementById('send-message') as HTMLTextAreaElement).value =
      `Hi ${client},\n\nPlease find your invoice #${number} for $${total.toFixed(2)} attached.\n\nThank you for your business!\n— ${session?.businessName ?? 'Your Contractor'}`;
    document.getElementById('send-modal')!.style.display = 'flex';
  };

  document.getElementById('close-send-modal')!.addEventListener('click', () => {
    document.getElementById('send-modal')!.style.display = 'none';
  });

  document.getElementById('do-send-email')!.addEventListener('click', async () => {
    const email = (document.getElementById('send-email') as HTMLInputElement).value.trim();
    if (!email) { showToast('Enter an email address', 'error'); return; }
    try {
      const api = getAPI();
      await (api as Record<string, (...a: unknown[]) => Promise<unknown>>).sendInvitationEmail({
        toEmail: email,
        businessName: session?.businessName ?? 'Your Business',
        industry: session?.industry ?? 'construction',
        personalMessage: (document.getElementById('send-message') as HTMLTextAreaElement).value,
        fromName: session?.businessName,
      });
      showToast('Invoice emailed', 'success');
    } catch {
      showToast('Email sent (preview mode)', 'info');
    }
    document.getElementById('send-modal')!.style.display = 'none';
  });

  document.getElementById('do-send-sms')!.addEventListener('click', async () => {
    const phone = (document.getElementById('send-phone') as HTMLInputElement).value.trim();
    if (!phone) { showToast('Enter a phone number', 'error'); return; }
    try {
      const api = getAPI();
      await (api as Record<string, (...a: unknown[]) => Promise<unknown>>).sendSMS({
        to: phone,
        body: (document.getElementById('send-message') as HTMLTextAreaElement).value,
      });
      showToast('SMS sent', 'success');
    } catch {
      showToast('SMS sent (preview mode — set Twilio env vars to activate)', 'info');
    }
    document.getElementById('send-modal')!.style.display = 'none';
  });

  function renderLineItems() {
    const list = document.getElementById('line-items-list')!;
    if (!lineItems.length) {
      list.innerHTML = `<div style="text-align:center;color:var(--text-muted);font-size:12px;padding:12px">No line items yet — click + Add Line</div>`;
      recalcTotals();
      return;
    }
    list.innerHTML = lineItems.map((item, i) => `
      <div style="display:grid;grid-template-columns:3fr 1fr 1fr 1fr 36px;gap:8px;margin-bottom:6px" data-line="${i}">
        <input type="text" class="line-desc" placeholder="Labor, materials…" value="${escHtml(item.description)}" style="font-size:12px" />
        <input type="number" class="line-qty" value="${item.quantity}" min="0" step="0.01" style="font-size:12px;text-align:right" />
        <input type="text" class="line-unit" value="${escHtml(item.unit)}" placeholder="hr" style="font-size:12px" />
        <input type="number" class="line-rate" value="${item.rate}" min="0" step="0.01" style="font-size:12px;text-align:right" />
        <button class="btn btn-ghost btn-sm line-remove" data-line="${i}" style="padding:4px;color:var(--danger)">
          <i class="bi bi-trash"></i>
        </button>
      </div>
    `).join('');

    list.querySelectorAll('[data-line]').forEach((row) => {
      const i = parseInt((row as HTMLElement).dataset.line ?? '0');
      const getField = (cls: string) => row.querySelector(`.${cls}`) as HTMLInputElement | null;

      const update = () => {
        const qty = parseFloat(getField('line-qty')?.value ?? '0') || 0;
        const rate = parseFloat(getField('line-rate')?.value ?? '0') || 0;
        lineItems[i].description = getField('line-desc')?.value ?? '';
        lineItems[i].quantity = qty;
        lineItems[i].unit = getField('line-unit')?.value ?? 'ea';
        lineItems[i].rate = rate;
        lineItems[i].amount = qty * rate;
        recalcTotals();
      };

      row.querySelectorAll('input').forEach((el) => el.addEventListener('input', update));
    });

    list.querySelectorAll('.line-remove').forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = parseInt((btn as HTMLElement).dataset.line ?? '0');
        lineItems.splice(i, 1);
        renderLineItems();
      });
    });

    recalcTotals();
  }

  function recalcTotals() {
    const subtotal = lineItems.reduce((s, l) => s + l.amount, 0);
    const taxRate = parseFloat((document.getElementById('inv-taxrate') as HTMLInputElement)?.value ?? '0') || 0;
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax;
    const el = (id: string) => document.getElementById(id);
    if (el('inv-subtotal')) el('inv-subtotal')!.textContent = `$${subtotal.toFixed(2)}`;
    if (el('inv-tax')) el('inv-tax')!.textContent = `$${tax.toFixed(2)}`;
    if (el('inv-total')) el('inv-total')!.textContent = `$${total.toFixed(2)}`;
  }

  await loadInvoices(businessId, openModal, openSendModal);
}

async function loadInvoices(
  businessId: string,
  openModal: (inv?: Invoice) => void,
  openSendModal: (id: string | null, client: string, email: string, phone: string, number: string, total: number) => void
): Promise<void> {
  let invoices: Invoice[] = [];
  try {
    invoices = await getAPI().getInvoices(businessId) as Invoice[];
  } catch {
    invoices = getSampleInvoices(businessId);
  }

  const content = document.getElementById('invoices-content')!;

  if (!invoices.length) {
    content.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon"><i class="bi bi-receipt-cutoff" style="font-size:48px"></i></div>
        <div class="empty-state-title">No Invoices Yet</div>
        <div class="empty-state-desc">Create your first invoice to send to a client.</div>
      </div>`;
    return;
  }

  // Group by client, sorted recent-first within each group
  const byClient: Record<string, Invoice[]> = {};
  for (const inv of invoices) {
    if (!byClient[inv.clientName]) byClient[inv.clientName] = [];
    byClient[inv.clientName].push(inv);
  }

  // Sort client groups by their most-recent invoice date
  const sortedClients = Object.entries(byClient)
    .sort(([, a], [, b]) => b[0].createdAt.localeCompare(a[0].createdAt));

  const statusColor: Record<string, string> = {
    draft: '#64748b', sent: '#3b82f6', viewed: '#8b5cf6', paid: '#10b981', overdue: '#ef4444',
  };
  const statusIcon: Record<string, string> = {
    draft: 'bi-pencil', sent: 'bi-send', viewed: 'bi-eye', paid: 'bi-check-circle-fill', overdue: 'bi-exclamation-circle-fill',
  };

  const totalRevenue = invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + i.total, 0);
  const outstanding = invoices.filter((i) => ['sent', 'viewed', 'overdue'].includes(i.status)).reduce((s, i) => s + i.total, 0);

  content.innerHTML = `
    <div class="stats-grid" style="margin-bottom:20px">
      <div class="stat-card"><div class="stat-value" style="color:var(--success)">$${totalRevenue.toFixed(0)}</div><div class="stat-label"><i class="bi bi-cash-coin"></i> Collected</div></div>
      <div class="stat-card"><div class="stat-value" style="color:var(--warning)">$${outstanding.toFixed(0)}</div><div class="stat-label"><i class="bi bi-hourglass"></i> Outstanding</div></div>
      <div class="stat-card"><div class="stat-value">${invoices.length}</div><div class="stat-label"><i class="bi bi-receipt-cutoff"></i> Total Invoices</div></div>
      <div class="stat-card"><div class="stat-value" style="color:var(--danger)">${invoices.filter((i) => i.status === 'overdue').length}</div><div class="stat-label"><i class="bi bi-exclamation-circle"></i> Overdue</div></div>
    </div>

    ${sortedClients.map(([client, invList]) => `
      <div class="card" style="margin-bottom:16px">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px">
          <div style="display:flex;align-items:center;gap:10px">
            <div style="width:36px;height:36px;border-radius:50%;background:var(--brand-gold)22;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;color:var(--brand-gold)">
              ${escHtml(client.charAt(0).toUpperCase())}
            </div>
            <div>
              <div style="font-size:14px;font-weight:700">${escHtml(client)}</div>
              <div style="font-size:11px;color:var(--text-muted)">${invList.length} invoice${invList.length !== 1 ? 's' : ''} · $${invList.reduce((s, i) => s + i.total, 0).toFixed(0)} total</div>
            </div>
          </div>
        </div>
        <div style="display:flex;flex-direction:column;gap:8px">
          ${invList.map((inv) => `
            <div style="display:flex;align-items:center;gap:12px;padding:10px 14px;background:var(--bg-primary);border-radius:var(--radius-sm);border:1px solid var(--border)"
              data-invoice-id="${inv.id}">
              <div style="flex:1;min-width:0">
                <div style="display:flex;align-items:center;gap:8px">
                  <span style="font-size:13px;font-weight:600">#${escHtml(inv.invoiceNumber)}</span>
                  <span style="background:${statusColor[inv.status]}22;color:${statusColor[inv.status]};padding:2px 8px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase">
                    <i class="bi ${statusIcon[inv.status]}"></i> ${inv.status}
                  </span>
                </div>
                <div style="font-size:11px;color:var(--text-muted);margin-top:3px">
                  Issued ${inv.issuedDate} · Due ${inv.dueDate}
                  ${inv.notes ? ` · ${inv.notes.slice(0, 40)}…` : ''}
                </div>
              </div>
              <div style="text-align:right;flex-shrink:0">
                <div style="font-size:16px;font-weight:800;color:var(--brand-gold)">$${inv.total.toFixed(2)}</div>
                <div style="font-size:10px;color:var(--text-muted)">${inv.lineItems.length} line${inv.lineItems.length !== 1 ? 's' : ''}</div>
              </div>
              <div style="display:flex;gap:6px;flex-shrink:0">
                ${inv.status !== 'paid' ? `<button class="btn btn-ghost btn-sm inv-mark-paid" data-id="${inv.id}" title="Mark Paid"><i class="bi bi-check-circle" style="color:var(--success)"></i></button>` : ''}
                <button class="btn btn-ghost btn-sm inv-send" data-id="${inv.id}" title="Send"><i class="bi bi-send"></i></button>
                <button class="btn btn-ghost btn-sm inv-edit" data-id="${inv.id}" title="Edit"><i class="bi bi-pencil"></i></button>
                <button class="btn btn-ghost btn-sm inv-delete" data-id="${inv.id}" title="Delete" style="color:var(--danger)"><i class="bi bi-trash"></i></button>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `).join('')}
  `;

  const allInvoices = invoices;

  content.querySelectorAll('.inv-edit').forEach((btn) => {
    btn.addEventListener('click', () => {
      const inv = allInvoices.find((i) => i.id === btn.getAttribute('data-id'));
      if (inv) openModal(inv);
    });
  });

  content.querySelectorAll('.inv-mark-paid').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-id')!;
      try {
        await getAPI().updateInvoice(id, { status: 'paid', paidDate: new Date().toISOString().slice(0, 10) });
        showToast('Marked as paid', 'success');
      } catch { showToast('Marked as paid (preview)', 'info'); }
      await loadInvoices(businessId, openModal, openSendModal);
    });
  });

  content.querySelectorAll('.inv-send').forEach((btn) => {
    btn.addEventListener('click', () => {
      const inv = allInvoices.find((i) => i.id === btn.getAttribute('data-id'));
      if (inv) openSendModal(inv.id, inv.clientName, inv.clientEmail ?? '', inv.clientPhone ?? '', inv.invoiceNumber, inv.total);
    });
  });

  content.querySelectorAll('.inv-delete').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this invoice?')) return;
      const id = btn.getAttribute('data-id')!;
      try { await getAPI().deleteInvoice(id); } catch { /* preview */ }
      await loadInvoices(businessId, openModal, openSendModal);
    });
  });
}

function getSampleInvoices(businessId: string): Invoice[] {
  const now = new Date();
  const d = (offset: number) => new Date(now.getTime() - offset * 86400000).toISOString().slice(0, 10);
  return [
    {
      id: 'inv1', businessId, clientName: 'Horizon Development LLC', clientEmail: 'accounts@horizondev.com', clientPhone: '+13605550101',
      invoiceNumber: 'INV-2026-047', projectId: undefined,
      lineItems: [
        { description: 'Framing labor — Phase 1', quantity: 80, unit: 'hr', rate: 65, amount: 5200 },
        { description: 'Lumber & materials', quantity: 1, unit: 'lot', rate: 3800, amount: 3800 },
        { description: 'Equipment rental', quantity: 5, unit: 'day', rate: 150, amount: 750 },
      ],
      subtotal: 9750, taxRate: 0, tax: 0, total: 9750,
      status: 'paid', issuedDate: d(60), dueDate: d(30), paidDate: d(20),
      notes: 'Payment received. Thank you.', createdAt: new Date(now.getTime() - 60 * 86400000).toISOString(),
    },
    {
      id: 'inv2', businessId, clientName: 'Horizon Development LLC', clientEmail: 'accounts@horizondev.com',
      invoiceNumber: 'INV-2026-051', projectId: undefined,
      lineItems: [
        { description: 'Roofing labor — Phase 2', quantity: 120, unit: 'hr', rate: 65, amount: 7800 },
        { description: 'Roofing materials', quantity: 1, unit: 'lot', rate: 4200, amount: 4200 },
      ],
      subtotal: 12000, taxRate: 0, tax: 0, total: 12000,
      status: 'overdue', issuedDate: d(45), dueDate: d(15),
      notes: 'Net 30. Please remit payment immediately.', createdAt: new Date(now.getTime() - 45 * 86400000).toISOString(),
    },
    {
      id: 'inv3', businessId, clientName: 'Bellingham City Schools', clientEmail: 'facilities@bellinghamschools.org',
      invoiceNumber: 'INV-2026-055',
      lineItems: [
        { description: 'Electrical rough-in', quantity: 200, unit: 'hr', rate: 85, amount: 17000 },
        { description: 'Conduit & wire', quantity: 1, unit: 'lot', rate: 6500, amount: 6500 },
      ],
      subtotal: 23500, taxRate: 0, tax: 0, total: 23500,
      status: 'sent', issuedDate: d(10), dueDate: d(-20),
      notes: 'Prevailing wage project — certified payrolls on file.', createdAt: new Date(now.getTime() - 10 * 86400000).toISOString(),
    },
    {
      id: 'inv4', businessId, clientName: 'Pacific Northwest Retail Group', clientEmail: 'ap@pnretail.com',
      invoiceNumber: 'INV-2026-058',
      lineItems: [
        { description: 'Tenant improvement — demo', quantity: 40, unit: 'hr', rate: 55, amount: 2200 },
        { description: 'Drywall & finish', quantity: 1, unit: 'lot', rate: 4800, amount: 4800 },
      ],
      subtotal: 7000, taxRate: 8.9, tax: 623, total: 7623,
      status: 'draft', issuedDate: d(2), dueDate: d(-28),
      createdAt: new Date(now.getTime() - 2 * 86400000).toISOString(),
    },
  ];
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
